import type { ReactNode } from 'react';

export type DecisionCardItem = Readonly<{
  label: string;
  value: string;
  detail?: string;
}>;

export type DecisionComparisonRow = Readonly<{
  id: string;
  label: string;
  description?: string;
  values: readonly string[];
}>;

export function DecisionInsight({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="decision-insight">
      <h3>{title}</h3>
      <div>{children}</div>
    </div>
  );
}

export function DecisionCardGrid({
  title,
  description,
  items,
}: {
  title: string;
  description?: string;
  items: readonly DecisionCardItem[];
}) {
  return (
    <section className="decision-block" aria-label={title}>
      <div className="decision-block-heading">
        <h3>{title}</h3>
        {description ? <p>{description}</p> : null}
      </div>
      <div className="decision-card-grid">
        {items.map((item) => (
          <div className="decision-card" key={item.label}>
            <span>{item.label}</span>
            <strong>{item.value}</strong>
            {item.detail ? <small>{item.detail}</small> : null}
          </div>
        ))}
      </div>
    </section>
  );
}

export function DecisionComparisonTable({
  title,
  description,
  columns,
  rows,
}: {
  title: string;
  description?: string;
  columns: readonly string[];
  rows: readonly DecisionComparisonRow[];
}) {
  return (
    <section className="decision-block" aria-label={title}>
      <div className="decision-block-heading">
        <h3>{title}</h3>
        {description ? <p>{description}</p> : null}
      </div>
      <div
        className="decision-table-wrap"
        role="region"
        aria-label={`${title} 표`}
        tabIndex={0}
      >
        <table className="decision-table">
          <thead>
            <tr>
              <th scope="col">조건</th>
              {columns.map((column) => <th scope="col" key={column}>{column}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <th scope="row">
                  <span>{row.label}</span>
                  {row.description ? <small>{row.description}</small> : null}
                </th>
                {row.values.map((value, index) => (
                  <td key={`${row.id}-${columns[index] ?? index}`}>{value}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
